import { useState, useRef } from "react";
import { BarChart, Bar, XAxis, LabelList, ResponsiveContainer, YAxis } from "recharts";
import ChartLegend from "./ChartLegend";

const LABEL_FONT = "13px sans-serif";
const LABEL_PADDING = 5;

const getTextWidth = (() => {
  let canvas;
  return (text) => {
    if (typeof document === "undefined") return 0;
    if (!canvas) canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    ctx.font = LABEL_FONT;
    return ctx.measureText(text).width;
  };
})();

const COLORS = {
  paid: "#00994f",
  unpaid: "#888888",
};

const LEGEND_ITEMS = [
  { color: COLORS.paid, label: "Pagate" },
  { color: COLORS.unpaid, label: "Non pagate" },
];

const SegmentLabel = ({ x, y, width, value, count, fill }) => {
  if (!value) return null;
  const fullText = `${value.toFixed(1)}% (${count})`;
  const cx = x + width / 2;
  if (width < getTextWidth(fullText) + LABEL_PADDING) return null;
  const label = fullText;
  return (
    <g pointerEvents="none">
      <text x={cx} y={y + 20} fill={fill} fontSize={13} textAnchor="middle" dominantBaseline="middle">
        {label}
      </text>
    </g>
  );
};

const BarChartComponent = ({ width, height, data, hideName, showTotals, left, showLegend = false }) => {
  const [tooltipData, setTooltipData] = useState(null);
  const containerRef = useRef(null);

  const pinTooltip = (data, segmentData, event) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setTooltipData({
      ...segmentData,
      x: event.clientX - rect.left + 12,
      y: event.clientY - rect.top - 36,
    });
  };

  const processedData = data.map((item) => {
    const sent = item.sent ?? 0;
    const paid = item.paid ?? 0;
    const unpaid = Math.max(sent - paid, 0);
    return {
      ...item,
      paidP: sent > 0 ? (paid / sent) * 100 : 0,
      unpaidP: sent > 0 ? (unpaid / sent) * 100 : 0,
      unpaid,
    };
  });

  return (
    <div style={{ position: "relative" }} ref={containerRef}>
      {tooltipData && (
        <div style={{
          position: "absolute", top: tooltipData.y, left: tooltipData.x,
          background: "#fff", border: `1px solid ${tooltipData.color}`,
          padding: "4px 8px", borderRadius: 4, fontSize: 12,
          pointerEvents: "none", zIndex: 10, lineHeight: 1.4, whiteSpace: "nowrap",
        }}>
          <div style={{ color: tooltipData.color, fontWeight: "bold" }}>{tooltipData.label}</div>
          <div>{tooltipData.value.toFixed(1)}% ({tooltipData.count})</div>
        </div>
      )}
      <ResponsiveContainer width={width} height={height}>
        <BarChart
          layout="vertical"
          data={processedData}
          margin={{ top: 10, right: showTotals ? 80 : 20, left, bottom: 20 }}
        >
          <XAxis type="number" domain={[0, 100]} allowDataOverflow hide />
          <YAxis
            type="category"
            dataKey="code"
            hide={hideName}
            width={200}
            dx={-10}
            axisLine={false}
            tickLine={false}
          />

          {/* PAID */}
          <Bar
            dataKey="paidP" stackId="a" fill={COLORS.paid} barSize={40} stroke="#000" strokeWidth={1}
            isAnimationActive={false} cursor="pointer"
            onMouseEnter={(data, index, event) => pinTooltip(data, { label: "Pagate", value: data.paidP, count: data.paid, color: COLORS.paid }, event)}
            onMouseLeave={() => setTooltipData(null)}
          >
            <LabelList
              dataKey="paidP"
              content={({ x, y, width, value, index }) => (
                <SegmentLabel x={x} y={y} width={width} value={value} count={processedData[index].paid} fill="#fff" />
              )}
            />
          </Bar>

          {/* UNPAID */}
          <Bar
            dataKey="unpaidP" stackId="a" fill={COLORS.unpaid} barSize={40} stroke="#000" strokeWidth={1}
            isAnimationActive={false} cursor="pointer"
            onMouseEnter={(data, index, event) => pinTooltip(data, { label: "Non pagate", value: data.unpaidP, count: data.unpaid, color: COLORS.unpaid }, event)}
            onMouseLeave={() => setTooltipData(null)}
          >
            <LabelList
              dataKey="unpaidP"
              content={({ x, y, width, value, index }) => (
                <SegmentLabel x={x} y={y} width={width} value={value} count={processedData[index].unpaid} fill="#fff" />
              )}
            />
            {showTotals && (
              <LabelList
                dataKey="sent"
                content={({ x, y, width, index }) => (
                  <text x={x + width + 10} y={y + 20} fill="#333" fontSize={14} dominantBaseline="middle">
                    {processedData[index].sent}
                  </text>
                )}
              />
            )}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {showLegend && <ChartLegend items={LEGEND_ITEMS} />}
    </div>
  );
};

export default BarChartComponent;
